import { Link } from 'react-router-dom';
import { Sprout, Facebook, Twitter, Instagram, Mail, Phone, MapPin, Linkedin, Youtube } from 'lucide-react';
import { useSiteSettings } from '../lib/site-settings';

export default function Footer() {
  const { settings, loading } = useSiteSettings();

  if (loading) {
    return (
      <footer className="bg-brand-950 text-brand-100">
        <div className="container-prose py-16">
          <div className="space-y-4">
            <div className="h-4 rounded bg-brand-900 w-1/2" />
            <div className="h-4 rounded bg-brand-900 w-3/4" />
            <div className="h-4 rounded bg-brand-900 w-full" />
          </div>
        </div>
      </footer>
    );
  }

  const {
    site_name,
    tagline,
    contact_email,
    contact_phone,
    contact_address,
    facebook_url,
    twitter_url,
    instagram_url,
    linkedin_url,
    whatsapp_number,
    organization_name,
  } = settings ?? {};

  return (
    <footer
      className="bg-brand-950 text-brand-100"
    >
      <div className="container-prose py-16">

        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">

          {/* Brand */}
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white">
                <Sprout className="h-6 w-6" />
              </div>
              <div>
                <p className="font-display text-lg font-bold text-white">
                  {site_name || 'Kisapuk'}
                </p>
                <p className="text-[10px] uppercase tracking-wider text-gold-400">
                  {organization_name || 'Community Initiative'}
                </p>
              </div>
            </div>

            <p className="text-sm text-brand-200">
              {tagline || 'Empowering Communities, Inspiring Change'}
            </p>
          </div>

          {/* Links */}
          <div>
            <h3 className="text-sm font-semibold text-white uppercase mb-4">
              Explore
            </h3>

            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="hover:text-gold-400">Home</Link></li>
              <li><Link to="/about" className="hover:text-gold-400">About</Link></li>
              <li><Link to="/programs" className="hover:text-gold-400">Programs</Link></li>
              <li><Link to="/contact" className="hover:text-gold-400">Contact</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm font-semibold text-white uppercase mb-4">
              Contact
            </h3>

            <div className="space-y-3 text-sm">

              {contact_email && (
                <div className="flex gap-2">
                  <Mail className="w-4 h-4 text-gold-400 mt-0.5" />
                  <a href={`mailto:${contact_email}`} className="hover:text-gold-400 break-all">
                    {contact_email}
                  </a>
                </div>
              )}

              {contact_phone && (
                <div className="flex gap-2">
                  <Phone className="w-4 h-4 text-gold-400 mt-0.5" />
                  <a href={`tel:${contact_phone}`} className="hover:text-gold-400">
                    {contact_phone}
                  </a>
                </div>
              )}

              {whatsapp_number && (
                <div className="flex gap-2">
                  <Phone className="w-4 h-4 text-gold-400 mt-0.5" />
                  <a href={`https://wa.me/${whatsapp_number.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="hover:text-gold-400">
                    WhatsApp: {whatsapp_number}
                  </a>
                </div>
              )}

              {contact_address && (
                <div className="flex gap-2">
                  <MapPin className="w-4 h-4 text-gold-400 mt-0.5" />
                  <span>{contact_address}</span>
                </div>
              )}
            </div>
          </div>

          {/* Social */}
          <div>
            <h3 className="text-sm font-semibold text-white uppercase mb-4">
              Follow Us
            </h3>

            <div className="flex flex-wrap gap-3">
              {facebook_url && (
                <a
                  href={facebook_url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Visit our Facebook page"
                  title="Visit our Facebook page"
                  className="rounded-full border border-brand-800 p-2 hover:text-gold-400 hover:border-gold-400"
                >
                  <Facebook className="h-4 w-4" />
                  <span className="sr-only">Facebook</span>
                </a>
              )}

              {twitter_url && (
                <a
                  href={twitter_url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Visit our X page"
                  title="Visit our X page"
                  className="rounded-full border border-brand-800 p-2 hover:text-gold-400 hover:border-gold-400"
                >
                  <Twitter className="h-4 w-4" />
                  <span className="sr-only">X</span>
                </a>
              )}

              {instagram_url && (
                <a
                  href={instagram_url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Visit our Instagram page"
                  title="Visit our Instagram page"
                  className="rounded-full border border-brand-800 p-2 hover:text-gold-400 hover:border-gold-400"
                >
                  <Instagram className="h-4 w-4" />
                  <span className="sr-only">Instagram</span>
                </a>
              )}

              {linkedin_url && (
                <a
                  href={linkedin_url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Visit our LinkedIn page"
                  title="Visit our LinkedIn page"
                  className="rounded-full border border-brand-800 p-2 hover:text-gold-400 hover:border-gold-400"
                >
                  <Linkedin className="h-4 w-4" />
                  <span className="sr-only">LinkedIn</span>
                </a>
              )}

              {settings.youtube_url && (
                <a
                  href={settings.youtube_url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Visit our YouTube channel"
                  title="Visit our YouTube channel"
                  className="rounded-full border border-brand-800 p-2 hover:text-gold-400 hover:border-gold-400"
                >
                  <Youtube className="h-4 w-4" />
                  <span className="sr-only">YouTube</span>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-6 border-t border-brand-800 text-sm text-brand-300">
          © {new Date().getFullYear()} {site_name || 'Kisapuk Community Initiative'} · Powered by <a href="https://ishoothmedia.co.ke" target="_blank" rel="noreferrer" className="text-gold-400 hover:text-gold-300">Ishoot Media Kenya</a>
        </div>
      </div>
    </footer>
  );
}